import * as React from "react";
import { useMemo, useState } from "react";
import styled from "styled-components";
import ArchiveCatalogTableEntry from "archive/components/ArchiveCatalogTableEntry";
import ArchiveCatalogExportModal from "archive/components/ArchiveCatalogExportModal";
import ArchiveActions from "archive/ArchiveActions";
import { PAGE_SIZES } from "archive/hooks/useArchivesQuery";
import { useArchivesQuery } from "archive/hooks";
import NumberUtils from "util/NumberUtils";
import {
  ConfirmDialog,
  IfPermitted,
  NoEntitiesExist,
  NoSearchResult,
  PaginatedList,
  SearchForm,
  Spinner
} from "components/common";
import { Table, DropdownButton, MenuItem, DeleteMenuItem } from "components/bootstrap";
import { IndicesActions } from "stores/indices/IndicesStore";
import usePaginationQueryParameter from "hooks/usePaginationQueryParameter";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import QueryHelper from "components/common/QueryHelper";
import OverlayTrigger from "components/common/OverlayTrigger";
import { StyledCatalogWrapperComponent } from "./StyledCatalogComponents";
const StyledDropDownbutton = styled(DropdownButton)`
  margin-left: 5px;
`;
const StyledPaginatedListContainer = styled.div`
  margin-top: 32px;
`;
const fieldMap = {
  index: "Index name of an archive",
  id: "Archive ID",
  nodes: "Server node names for archived messages",
  streams: "Stream names for archived messages",
  sources: "Source names in archived messages",
  created: "Archive creation date",
  from: "Minimum message timestamp in archive",
  to: "Maximum message timestamp in archive"
};
const queryExamples = /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, "Find archives which contain messages in a date range:", /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, "from:>=2017-03-23 to:<=2017-04-01"), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, 'from:">=2016-02-18 18:10:37.055" to:"<=2016-02-18 18:13:16.368"')), /* @__PURE__ */ React.createElement("p", null, "Find archives which contain messages from the ", /* @__PURE__ */ React.createElement("var", null, "errors"), " stream:", /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, "streams:errors")), /* @__PURE__ */ React.createElement("p", null, "Find archives which contain messages from the ", /* @__PURE__ */ React.createElement("var", null, "errors"), " and ", /* @__PURE__ */ React.createElement("var", null, "warnings"), " streams:", /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, "streams:errors streams:warnings"), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, "streams:errors,warnings")), /* @__PURE__ */ React.createElement("p", null, "Find archives which contain messages from the ", /* @__PURE__ */ React.createElement("var", null, "foo"), " index and the ", /* @__PURE__ */ React.createElement("var", null, "errors"), " stream:", /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, "index:foo streams:errors"), /* @__PURE__ */ React.createElement("br", null)));
const queryHelpComponent = /* @__PURE__ */ React.createElement(QueryHelper, { entityName: "archives", fieldMap, example: queryExamples, commonFields: [] });
const ArchiveCatalog = () => {
  var _a;
  const { page, pageSize: per_page, resetPage } = usePaginationQueryParameter(PAGE_SIZES, (_a = PAGE_SIZES) == null ? void 0 : _a[0]);
  const [query, setQuery] = useState("");
  const [markedArchives, setMarkedArchives] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmRestore, setShowConfirmRestore] = useState(false);
  const [showAlreadyRestoredArchivesToRestore, setShowAlreadyRestoredArchivesToRestore] = useState(false);
  const [showArchivesMarkedToRestoreWithSameIndexName, setShowArchivesMarkedToRestoreWithSameIndexName] = useState(false);
  const sendTelemetry = useSendTelemetry();
  const queryParams = useMemo(() => ({ page, per_page, query }), [page, per_page, query]);
  const {
    isLoading,
    data: { archives, archives_context: archivesContext, backends_context: backendsContext, total },
    refetch
  } = useArchivesQuery(queryParams);
  const onPaginationChange = () => {
    setMarkedArchives([]);
  };
  const onSearch = (newQuery, resetLoadingStateCb) => {
    resetPage();
    setQuery(newQuery);
    resetLoadingStateCb();
  };
  const onSearchReset = () => {
    resetPage();
    setQuery("");
  };
  const onMarkArchive = (event) => {
    const { checked, name } = event.target;
    if (checked) {
      setMarkedArchives([...markedArchives, name]);
    } else {
      setMarkedArchives(markedArchives.filter((archiveId) => archiveId !== name));
    }
  };
  const checkedMarkedArchive = (archiveId) => markedArchives.includes(archiveId);
  const getMarkedArchiveObjects = () => markedArchives.map((archiveId) => archives.find((archive) => archive.id === archiveId)).filter((archive) => archive !== void 0);
  const deleteArchive = (archive) => {
    ArchiveActions.deleteArchive(archive.backend_id, archive.archive_id).then(() => {
      const archiveContext = archivesContext[archive.backend_id][archive.archive_id];
      refetch();
      if (archiveContext.restored) {
        IndicesActions.delete(archiveContext.restored_index_name);
      }
    });
  };
  const restoreArchives = (archivesToRestore) => {
    ArchiveActions.restoreArchives(archivesToRestore).then(() => {
      refetch();
    });
  };
  const deleteSelectedArchives = () => {
    setShowConfirmDelete(true);
  };
  const confirmDeleteSelectedArchives = () => {
    setShowConfirmDelete(false);
    getMarkedArchiveObjects().forEach((archive) => {
      deleteArchive(archive);
    });
    setMarkedArchives([]);
    sendTelemetry(TELEMETRY_EVENT_TYPE.ARCHIVE.DELETED, {
      app_pathname: "archive",
      app_section: "archive-catalog"
    });
  };
  const cancelDeleteSelectedArchives = () => {
    setShowConfirmDelete(false);
  };
  const getMarkedAlreadyRestoredArchives = () => getMarkedArchiveObjects().filter((archive) => {
    const archiveContext = archivesContext[archive.backend_id][archive.archive_id];
    return !!archiveContext.restored;
  });
  const getArchivesByIndexName = () => {
    const byIndexName = {};
    getMarkedArchiveObjects().forEach((archive) => {
      if (byIndexName[archive.index_name] === void 0) {
        byIndexName[archive.index_name] = [];
      }
      byIndexName[archive.index_name].push(archive);
    });
    return byIndexName;
  };
  const restoreSelectedArchives = () => {
    const multipleArchivesWithSameIndexNameMarked = Object.values(getArchivesByIndexName()).find(
      (archiveList) => archiveList.length > 1
    );
    if (getMarkedAlreadyRestoredArchives().length > 0) {
      setShowAlreadyRestoredArchivesToRestore(true);
    } else if (multipleArchivesWithSameIndexNameMarked) {
      setShowArchivesMarkedToRestoreWithSameIndexName(true);
    } else {
      setShowConfirmRestore(true);
    }
  };
  const confirmRestoreSelectedArchives = () => {
    setShowConfirmRestore(false);
    const archivesToRestore = getMarkedArchiveObjects();
    restoreArchives(archivesToRestore);
    setMarkedArchives([]);
    sendTelemetry(TELEMETRY_EVENT_TYPE.ARCHIVE.RESTORED, {
      app_pathname: "archive",
      app_section: "archive-catalog"
    });
  };
  const cancelRestoreSelectedArchives = () => {
    setShowConfirmRestore(false);
  };
  const hideAlreadyRestoredArchivesToRestoreDialog = () => {
    setShowAlreadyRestoredArchivesToRestore(false);
  };
  const hideArchivesMarkedToRestoreWithSameIndexNameDialog = () => {
    setShowArchivesMarkedToRestoreWithSameIndexName(false);
  };
  const sizeTextForArchive = (archive) => {
    const { index_size, segments } = archive;
    if (index_size > 0) {
      return ` (index size ${NumberUtils.formatBytes(index_size)})`;
    }
    const segmentRawSize = segments.reduce((sum, segment) => sum + segment.raw_size, 0);
    if (segmentRawSize > 0) {
      return ` (uncompressed size: ${NumberUtils.formatBytes(segmentRawSize)})`;
    }
    return "";
  };
  const totalSizeTextForArchives = (archivesToRestore) => {
    const totalIndexSize = archivesToRestore.reduce((previousSize, archive) => archive.index_size + previousSize, 0);
    if (totalIndexSize > 0) {
      return ` with estimated total size ${NumberUtils.formatBytes(totalIndexSize)}`;
    }
    const segmentRawSize = archivesToRestore.reduce((previousSize, archive) => {
      const segmentSize = archive.segments.reduce((sum, segment) => sum + segment.raw_size, 0);
      return segmentSize + previousSize;
    }, 0);
    if (segmentRawSize > 0) {
      return ` with estimated total uncompressed size ${NumberUtils.formatBytes(segmentRawSize)}`;
    }
    return "";
  };
  const restoreTextForArchive = (archive) => {
    const archiveContext = archivesContext[archive.backend_id][archive.archive_id];
    return archiveContext.restored ? " (including the restored index)" : "";
  };
  const getArchiveRow = (archive, { withSize = false, withRestore = false } = {}) => /* @__PURE__ */ React.createElement("li", { key: archive.archive_id }, archive.archive_id, " (", archive.index_name, ")", withRestore && restoreTextForArchive(archive), withSize && sizeTextForArchive(archive));
  const archivesRows = archives.map((archive) => /* @__PURE__ */ React.createElement(
    ArchiveCatalogTableEntry,
    {
      key: `archive-entry-${archive.id}`,
      archive,
      archiveContext: archivesContext[archive.backend_id][archive.archive_id],
      backendContext: backendsContext[archive.backend_id],
      onMarkArchive,
      checkedMarkedArchive
    }
  ));
  const markedArchivesForBulkActions = getMarkedArchiveObjects();
  const dialogs = /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: showConfirmDelete,
      onConfirm: confirmDeleteSelectedArchives,
      onCancel: cancelDeleteSelectedArchives,
      title: "Delete archives"
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, "You are about to delete ", markedArchivesForBulkActions.length, " archives, including their restored indices. This action cannot be undone."), /* @__PURE__ */ React.createElement("p", null, "Archives to be deleted:"), /* @__PURE__ */ React.createElement("ul", { className: "pill-list" }, markedArchivesForBulkActions.map((archive) => getArchiveRow(archive, { withRestore: true }))), /* @__PURE__ */ React.createElement("p", null), /* @__PURE__ */ React.createElement("p", null, "Are you sure you want to continue?"))
  ), /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: showConfirmRestore,
      onConfirm: confirmRestoreSelectedArchives,
      onCancel: cancelRestoreSelectedArchives,
      title: "Restore archives"
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, "You are about to restore ", markedArchivesForBulkActions.length, " archives into new indices", " ", totalSizeTextForArchives(markedArchivesForBulkActions), "."), /* @__PURE__ */ React.createElement("p", null, "Archives to be restored:"), /* @__PURE__ */ React.createElement("ul", { className: "pill-list" }, markedArchivesForBulkActions.map((archive) => getArchiveRow(archive, { withSize: true }))), /* @__PURE__ */ React.createElement("p", null), /* @__PURE__ */ React.createElement("p", null, "Are you sure you want to continue restoring?"))
  ), /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      hideCancelButton: true,
      btnConfirmText: "Okay",
      show: showAlreadyRestoredArchivesToRestore,
      onConfirm: hideAlreadyRestoredArchivesToRestoreDialog,
      title: "Restore archives"
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, "Some of the selected archives are already restored."), /* @__PURE__ */ React.createElement("p", null, "Archives already restored:"), /* @__PURE__ */ React.createElement("ul", { className: "pill-list" }, getMarkedAlreadyRestoredArchives().map((archive) => getArchiveRow(archive))), /* @__PURE__ */ React.createElement("p", null), /* @__PURE__ */ React.createElement("p", null, "Please unmark them and try again."))
  ), /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      hideCancelButton: true,
      btnConfirmText: "Okay",
      show: showArchivesMarkedToRestoreWithSameIndexName,
      onConfirm: hideArchivesMarkedToRestoreWithSameIndexNameDialog,
      title: "Restore archives"
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, "Some of the selected archives would be restored in an index an identical name."), /* @__PURE__ */ React.createElement("p", null, "Archives with same index name:"), /* @__PURE__ */ React.createElement("ul", { className: "pill-list" }, Object.values(getArchivesByIndexName()).flat().map((archive) => getArchiveRow(archive))))
  ));
  const toolltip = /* @__PURE__ */ React.createElement(React.Fragment, null, "Please mark some archives");
  return isLoading ? /* @__PURE__ */ React.createElement(Spinner, { text: "Loading archive catalog..." }) : /* @__PURE__ */ React.createElement(StyledCatalogWrapperComponent, null, dialogs, /* @__PURE__ */ React.createElement("h2", null, "Archive Catalog", /* @__PURE__ */ React.createElement("span", null, "\xA0", /* @__PURE__ */ React.createElement("small", null, total, " total (", markedArchivesForBulkActions.length, " marked)"))), /* @__PURE__ */ React.createElement(StyledPaginatedListContainer, null, /* @__PURE__ */ React.createElement(PaginatedList, { totalItems: total, pageSizes: PAGE_SIZES, pageSize: per_page, onChange: onPaginationChange }, /* @__PURE__ */ React.createElement(
    SearchForm,
    {
      onSearch,
      onReset: onSearchReset,
      useLoadingState: true,
      queryHelpComponent
    },
    /* @__PURE__ */ React.createElement(StyledDropDownbutton, { keepMounted: true, id: "export-results-dropdown", title: "Export Results" }, /* @__PURE__ */ React.createElement(ArchiveCatalogExportModal, { query: query || "" })),
    /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["archive:delete", "archive:restore"], anyPermissions: true }, /* @__PURE__ */ React.createElement(
      OverlayTrigger,
      {
        overlay: toolltip,
        trigger: ["hover", "focus"],
        disabled: markedArchives.length > 0,
        placement: "top"
      },
      /* @__PURE__ */ React.createElement(
        StyledDropDownbutton,
        {
          disabled: markedArchives.length === 0,
          id: "bulk-actions-dropdown",
          title: "Bulk Actions"
        },
        /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "archive:delete" }, /* @__PURE__ */ React.createElement(DeleteMenuItem, { onClick: deleteSelectedArchives }), /* @__PURE__ */ React.createElement(MenuItem, { divider: true })),
        /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "archive:restore" }, /* @__PURE__ */ React.createElement(MenuItem, { onClick: restoreSelectedArchives }, "Restore"))
      )
    ))
  ), !total && !query && /* @__PURE__ */ React.createElement(NoEntitiesExist, null, "No archive catalogs have been created yet."), !total && query && /* @__PURE__ */ React.createElement(NoSearchResult, null, "No archive catalogs have been found."), !!total && /* @__PURE__ */ React.createElement(Table, { condensed: true, hover: true }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["archive:delete", "archive:restore"], anyPermissions: true }, /* @__PURE__ */ React.createElement("th", null, "Mark")), /* @__PURE__ */ React.createElement("th", null, "Index"), /* @__PURE__ */ React.createElement("th", null, "Backend"), /* @__PURE__ */ React.createElement("th", null, "Created"), /* @__PURE__ */ React.createElement("th", null, "Range"), /* @__PURE__ */ React.createElement("th", null, "Content"), /* @__PURE__ */ React.createElement("th", null, "Streams"), /* @__PURE__ */ React.createElement("th", { className: "restored" }, "Restored"))), archivesRows))));
};
export default ArchiveCatalog;
